import { useCallback, useMemo, useRef } from 'react';

import { IAppboy } from 'appboy-web-sdk';

import { useApiKey } from 'hooks/configs/use-api-key';
import { useConfigValue } from 'hooks/configs/use-config-value';
import useEffectOnUnmount from 'hooks/use-effect-on-unmount';
import useEffectOnce from 'hooks/use-effect-once';
import useReadyQueue from 'hooks/use-ready-queue';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import ReactAppboy, { ContentCard } from 'utils/appboy';
import { isRecentCookieTimestamp } from 'utils/cookies';
import logger from 'utils/logger';

import { IBrazeContext } from '../types';

const useBraze = () => {
  const { enqueueIfNotDrained, drainQueue } = useReadyQueue();
  const enableCookieBanner = useFlag(LaunchDarklyFlag.ENABLE_COOKIE_BANNER);
  const enableHtmlInAppMessagesInBrazeSdkConfig = useFlag(
    LaunchDarklyFlag.ENABLE_HTML_IN_APP_MESSAGES_IN_BRAZE_SDK
  );
  const brazeKey = useApiKey({ key: 'braze' });
  const brazeUrl = useConfigValue({ key: 'urls', defaultValue: {} }).braze;

  const hasAcceptedCookies = isRecentCookieTimestamp();

  const toggleContentCards = useCallback<IBrazeContext['toggleContentCards']>(
    enqueueIfNotDrained((parentNode: Element) => {
      // @ts-ignore TS is reading types from mobile file. Signatures are different compared with ReactAppboy.web
      ReactAppboy.launchContentCards(parentNode);
    }),
    [enqueueIfNotDrained]
  );

  const setUserId = useCallback<IBrazeContext['setUserId']>(
    enqueueIfNotDrained((userId: string | null) => {
      // @ts-ignore TS is reading types from mobile file. Signatures are different compared with ReactAppboy.web
      ReactAppboy.changeUser(userId);
      return ReactAppboy.requestContentCardsRefresh();
    }),
    []
  );

  // Making this async to be compatible with the react native module
  const getCachedContentCards = useCallback<IBrazeContext['getCachedContentCards']>(async () => {
    // @ts-ignore TS is reading types from mobile file. Signatures are different compared with ReactAppboy.web
    const { cards = [] } = ReactAppboy.getContentCards() || {};
    return cards as ContentCard[];
  }, []);

  const initBrazeRef = useRef(() => {});
  const initWasCalled = useRef(false);

  initBrazeRef.current = () => {
    import('../init')
      .then(({ default: init }) => {
        if (initWasCalled.current) {
          return;
        }
        init(brazeKey, brazeUrl, drainQueue, {
          enableHtmlInAppMessagesInBrazeSdkConfig,
        });
        initWasCalled.current = true;
      })
      .catch(error => {
        logger.error({ error, message: 'Braze: import error' });
      });
  };

  // TODO: check what should happen when the deps changes. Should this hook act reactively?
  useEffectOnce(() => {
    if (!enableCookieBanner || hasAcceptedCookies) {
      initBrazeRef.current?.();
    }
  });

  useEffectOnUnmount(() => {
    // appboy requests are batched and flushed.
    // if the app is unmounting we should immediately
    // send anything in the queue
    ReactAppboy.requestImmediateDataFlush();
  });

  const ctxValue: IBrazeContext = useMemo(
    () => ({
      initBraze: initBrazeRef.current,
      getCachedContentCards,
      setUserId,
      toggleContentCards,
    }),
    [getCachedContentCards, setUserId, toggleContentCards]
  );

  return ctxValue;
};

export default useBraze;

declare global {
  interface Window {
    appboy: IAppboy;
    rbiAppboy?: IAppboy;
    rbiAppboyInit?: () => void;
  }
}
